import { createContext, useContext, useEffect, useRef, useState } from 'react';

/**
 * The current page tells the favorites system who it is, via `useFavoritable`.
 * The TopBar reads `name` from this context and persists `name + path` when the
 * star is clicked. If a page never registers a name, the TopBar hides the star.
 *
 * Pages register/unregister on mount/unmount. When two siblings race, the most
 * recent `setName` wins (LIFO) and the previous name is restored on unmount.
 */
interface FavoritableCtx {
  name: string | null;
  setName: (name: string | null) => void;
}

const Ctx = createContext<FavoritableCtx | null>(null);

export function FavoritableProvider({ children }: { children: React.ReactNode }) {
  const [name, setName] = useState<string | null>(null);
  return <Ctx.Provider value={{ name, setName }}>{children}</Ctx.Provider>;
}

export function useFavoritableState(): FavoritableCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useFavoritableState must be used inside <FavoritableProvider>');
  return ctx;
}

/**
 * Page-level: declare the current page as favoritable.
 *
 *   useFavoritable({ name: `Brand · ${brand.name}` });
 *
 * Passing `null`/empty hides the star. The hook restores the previous name on
 * unmount so navigating away from a nested screen doesn't leak its label.
 */
export function useFavoritable(input: { name: string | null | undefined } | string | null) {
  const ctx = useContext(Ctx);
  const name =
    typeof input === 'string' || input === null
      ? input
      : input?.name ?? null;
  const prevRef = useRef<string | null>(null);

  useEffect(() => {
    if (!ctx) return;
    prevRef.current = ctx.name;
    ctx.setName(name && name.trim() ? name.trim() : null);
    return () => ctx.setName(prevRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);
}
